import { X, Keyboard } from "lucide-react";
import { useEffect, useRef } from "react";
import { isMac } from "../../utils/platform";
import { createPortal } from "react-dom";

interface Shortcut {
  category: string;
  shortcuts: {
    keys: string[];
    description: string;
    context?: string;
  }[];
}

const SHORTCUTS: Shortcut[] = [
  {
    category: "Global",
    shortcuts: [
      { keys: ["Cmd/Ctrl", "K"], description: "Open command palette (search)" },
      { keys: ["?"], description: "Show keyboard shortcuts" },
      { keys: ["Esc"], description: "Close modal / Cancel action" },
    ],
  },
  {
    category: "Quick Navigation (press G, then key)",
    shortcuts: [
      { keys: ["G", "D"], description: "Go to Dashboard" },
      { keys: ["G", "C"], description: "Go to CPI Dashboard" },
      { keys: ["G", "R"], description: "Go to Review Queue" },
      { keys: ["G", "A"], description: "Go to AI Systems" },
      { keys: ["G", "T"], description: "Go to Decision Log" },
      { keys: ["G", "P"], description: "Go to Policy Engine" },
      { keys: ["G", "N"], description: "Go to Anomalies" },
      { keys: ["G", "F"], description: "Go to Feedback Analytics" },
      { keys: ["G", "I"], description: "Go to Integration Hub" },
      { keys: ["G", "B"], description: "Go to Billing" },
      { keys: ["G", "S"], description: "Go to Settings" },
    ],
  },
  {
    category: "Actions",
    shortcuts: [
      { keys: ["N"], description: "New / Create", context: "In lists" },
      { keys: ["Cmd/Ctrl", "S"], description: "Save changes", context: "In modals/forms" },
      { keys: ["Cmd/Ctrl", "Enter"], description: "Submit / Confirm", context: "In modals/forms" },
      { keys: ["Delete"], description: "Delete selected item" },
    ],
  },
  {
    category: "Tables & Lists",
    shortcuts: [
      { keys: ["↑", "↓"], description: "Navigate rows" },
      { keys: ["Enter"], description: "Open selected item" },
      { keys: ["Cmd/Ctrl", "A"], description: "Select all" },
      { keys: ["Shift", "Click"], description: "Multi-select" },
    ],
  },
  {
    category: "Modals & Forms",
    shortcuts: [
      { keys: ["Tab"], description: "Next field" },
      { keys: ["Shift", "Tab"], description: "Previous field" },
      { keys: ["Esc"], description: "Close without saving" },
      { keys: ["Cmd/Ctrl", "Enter"], description: "Submit form" },
    ],
  },
  {
    category: "Filtering & Search",
    shortcuts: [
      { keys: ["F"], description: "Toggle filters", context: "In data views" },
      { keys: ["Cmd/Ctrl", "F"], description: "Focus search" },
      { keys: ["Cmd/Ctrl", "Shift", "F"], description: "Clear filters" },
    ],
  },
];

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Focusable-element query for the focus trap. Standard pattern lifted
// from WAI-ARIA APG. tabindex="-1" is excluded (programmatic focus only,
// not Tab-stop).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // WAI-ARIA dialog pattern: capture invoker on open + restore on
  // close so keyboard users don't get dumped to <body>. Pre-fix focus
  // dropped to <body> after ESC, forcing them to Tab from the top of
  // the page back to wherever they were.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Close: restore focus to the invoker (guard for unmounted invoker).
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) prev.focus();
      return;
    }

    // Open: snapshot invoker + move focus to the close button.
    previousFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    closeButtonRef.current?.focus();

    // Real focus trap (future-proof for additional focusable elements).
    // Pre-fix aria-modal=true declared a focus-trap contract but Tab
    // moved focus OUT of the modal (the prior single-element loop only
    // worked because there was only one button — adding any link or
    // input later would have silently broken the trap).
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // Body scroll-lock — modal-pattern hygiene. Without it, scrolling
    // with arrow keys / page-down propagates to the underlying page
    // even though the modal claims focus.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatKey = (key: string) => {
    if (key === "Cmd/Ctrl") return isMac ? "⌘" : "Ctrl";
    if (key === "Enter") return "↵";
    if (key === "Delete") return "Del";
    if (key === "Shift") return "⇧";
    if (key === "Tab") return "⇥";
    if (key === "Esc") return "Esc";
    return key;
  };

  // Portal to document.body so the modal isn't trapped in a transformed
  // ancestor's stacking context (scale/translate parents create a new
  // containing block, breaking position:fixed for the backdrop).
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 motion-reduce:transition-none"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="kbd-modal-title"
        className="adp-modal relative w-[90vw] max-w-[860px] max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 border bg-surface-1 border-border">
              <Keyboard aria-hidden="true" className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <h2 id="kbd-modal-title" className="text-lg font-semibold text-foreground">
                KEYBOARD SHORTCUTS
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Speed up your workflow with keyboard commands
              </p>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-surface-2 border border-transparent hover:border-border transition-colors motion-reduce:transition-none flex-shrink-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
            aria-label="Close keyboard shortcuts"
          >
            <X aria-hidden="true" className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content. Each category renders as a section with a heading
            and a <ul> of shortcuts. Pre-fix the structure was nested
            divs — invisible to SR list-rotor and "headings list" navigation. */}
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 py-6 flex-1 overflow-y-auto">
          {SHORTCUTS.map((category) => {
            // Slug cleanup: strip non-alphanumeric (parens, commas) so
            // ids like "kbd-cat-quick-navigation-(press-g,-then-key)"
            // become "kbd-cat-quick-navigation-press-g-then-key".
            // Cleaner DOM + safer if any future selector uses these ids.
            const slug = category.category
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '');
            const headingId = `kbd-cat-${slug}`;
            return (
            <section key={category.category} aria-labelledby={headingId}>
              <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="h-px flex-1 bg-border" aria-hidden="true" />
                <h3 id={headingId} className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider font-semibold">
                  {category.category}
                </h3>
                <div className="h-px flex-1 bg-border" aria-hidden="true" />
              </div>

              <ul className="space-y-2 list-none p-0 m-0">
                {category.shortcuts.map((shortcut, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between py-2 px-3 border border-transparent"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-foreground">
                        {shortcut.description}
                      </div>
                      {shortcut.context && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {shortcut.context}
                        </div>
                      )}
                    </div>
                    {/* aria-label uses RAW key names (Cmd/Ctrl, Shift,
                        Tab) instead of formatted symbols (⌘, ⇧, ⇥). SR
                        announcement of the symbol can be unreliable
                        ("image" / "command" / depends on AT). Raw names
                        always announce as words. */}
                    <div className="flex items-center gap-1.5" aria-label={`Keys: ${shortcut.keys.join(' plus ')}`}>
                      {shortcut.keys.map((key, keyIdx) => (
                        <div key={keyIdx} className="flex items-center gap-1">
                          {keyIdx > 0 && (
                            <span aria-hidden="true" className="text-xs text-muted-foreground">+</span>
                          )}
                          <kbd className="px-2 py-1 bg-background border border-border text-xs text-mono text-foreground min-w-[28px] text-center">
                            {formatKey(key)}
                          </kbd>
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
            );
          })}
        </div>

      </div>
    </div>,
    document.body
  );
}